import { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { LanguageSwitch } from '../components/LanguageSwitch';
import Logo from '../components/Logo';
import { ArrowRight } from 'lucide-react';
import ReCAPTCHA from 'react-google-recaptcha';

function Register() {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [firstNameError, setFirstNameError] = useState('');
  const [lastNameError, setLastNameError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');
  const [loading, setLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [captchaError, setCaptchaError] = useState('');
  const [toast, setToast] = useState({ message: null, hiding: false });
  const toastTimeoutRef = useRef(null);
  const toastFadeRef = useRef(null);
  const recaptchaRef = useRef(null);
  const { register } = useAuth();
  const { t } = useLanguage();

  const showToast = (message) => {
    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    if (toastFadeRef.current) clearTimeout(toastFadeRef.current);
    setToast({ message, hiding: false });
    toastTimeoutRef.current = setTimeout(() => {
      setToast((prev) => (prev.message ? { ...prev, hiding: true } : prev));
      toastFadeRef.current = setTimeout(() => {
        setToast({ message: null, hiding: false });
        toastTimeoutRef.current = null;
        toastFadeRef.current = null;
      }, 1000);
    }, 5000);
  };

  useEffect(() => {
    return () => {
      if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
      if (toastFadeRef.current) clearTimeout(toastFadeRef.current);
    };
  }, []);
  const navigate = useNavigate();

  // Get reCAPTCHA site key from environment variable
  const RECAPTCHA_SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY || '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI'; // Google's test key

  const validateEmail = (emailValue) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailValue) {
      return t('login.emailRequired');
    }
    if (!emailRegex.test(emailValue)) {
      return t('login.emailInvalid');
    }
    return '';
  };

  const validateFirstName = (value) => {
    if (!value.trim()) {
      return t('register.firstNameRequired');
    }
    return '';
  };

  const validateLastName = (value) => {
    if (!value.trim()) {
      return t('register.lastNameRequired');
    }
    return '';
  };

  const validatePassword = (passwordValue) => {
    if (!passwordValue) {
      return t('login.passwordRequired');
    }
    if (passwordValue.length < 8) {
      return t('login.passwordMinLength');
    }
    if (!/[A-Z]/.test(passwordValue)) {
      return t('login.passwordUppercase');
    }
    if (!/[0-9]/.test(passwordValue)) {
      return t('login.passwordNumber');
    }
    if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(passwordValue)) {
      return t('login.passwordSpecial');
    }
    return '';
  };

  const validateConfirmPassword = (confirmPasswordValue) => {
    if (!confirmPasswordValue) {
      return t('register.confirmPasswordRequired');
    }
    if (confirmPasswordValue !== password) {
      return t('register.passwordsMismatch');
    }
    return '';
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    setEmailError(validateEmail(value));
  };

  const handleFirstNameChange = (e) => {
    const value = e.target.value;
    setFirstName(value);
    setFirstNameError(validateFirstName(value));
  };

  const handleLastNameChange = (e) => {
    const value = e.target.value;
    setLastName(value);
    setLastNameError(validateLastName(value));
  };

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setPassword(value);
    setPasswordError(validatePassword(value));
    // Re-validate confirm password if it's already filled
    if (confirmPassword) {
      setConfirmPasswordError(validateConfirmPassword(confirmPassword));
    }
  };

  const handleConfirmPasswordChange = (e) => {
    const value = e.target.value;
    setConfirmPassword(value);
    setConfirmPasswordError(validateConfirmPassword(value));
  };

  const handleCaptchaChange = (token) => {
    setCaptchaToken(token);
    setCaptchaError('');
  };

  const handleCaptchaExpired = () => {
    setCaptchaToken(null);
    setCaptchaError('');
  };

  const handleCaptchaError = () => {
    setCaptchaToken(null);
    setCaptchaError(t('register.captchaFailed'));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setCaptchaError('');

    const firstNameErr = validateFirstName(firstName);
    const lastNameErr = validateLastName(lastName);
    const emailErr = validateEmail(email);
    const passwordErr = validatePassword(password);
    const confirmPasswordErr = validateConfirmPassword(confirmPassword);

    setFirstNameError(firstNameErr);
    setLastNameError(lastNameErr);
    setEmailError(emailErr);
    setPasswordError(passwordErr);
    setConfirmPasswordError(confirmPasswordErr);

    if (firstNameErr || lastNameErr || emailErr || passwordErr || confirmPasswordErr) {
      return;
    }

    // Validate CAPTCHA
    if (!captchaToken) {
      setCaptchaError(t('register.captchaRequired'));
      return;
    }

    setLoading(true);

    try {
      await register(email, password, captchaToken, firstName.trim(), lastName.trim());
      setError('');
      showToast(t('register.successToast'));
      // Let the user see the toast, then go to login
      setTimeout(() => navigate('/login'), 2500);
    } catch (err) {
      setError(err.response?.data?.error || err.message || t('register.errorFailed'));
      // Reset CAPTCHA on error
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
        setCaptchaToken(null);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#FFFFFF', color: '#1B211A' }}>
      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-xl" style={{ borderBottom: '1px solid rgba(169, 201, 232, 0.3)', backgroundColor: 'rgba(255, 255, 255, 0.95)' }}>
<div className="max-w-6xl mx-auto px-4 h-12 flex items-center justify-between gap-4">
            <Logo asLink to="/" />
          <div className="flex items-center gap-2">
            <LanguageSwitch />
            <Link 
              to="/" 
              className="text-xs md:text-sm transition-colors hover:opacity-80"
              style={{ color: 'rgb(100, 130, 160)' }}
            >
              {t('common.backToHome')}
            </Link>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 flex items-center justify-center px-4 py-10 md:py-16">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <h1 className="text-3xl md:text-4xl font-semibold tracking-tight mb-2" style={{ color: '#1B211A' }}>
              {t('auth.createAccount')}
            </h1>
            <p className="text-sm" style={{ color: 'rgb(100, 130, 160)' }}>
              {t('register.subtitle')}
            </p>
          </div>

          <div className="rounded-2xl p-6 md:p-8" style={{ backgroundColor: 'rgba(255, 255, 255, 0.6)', border: '1px solid rgba(169, 201, 232, 0.3)' }}>
            {error && (
              <div className="mb-4 p-3 rounded-lg text-xs" style={{ backgroundColor: 'rgba(220, 38, 38, 0.1)', color: '#dc2626', border: '1px solid rgba(220, 38, 38, 0.3)' }}>
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#1B211A' }}>
                    {t('register.firstName')}
                  </label>
                  <input
                    type="text"
                    value={firstName}
                    onChange={handleFirstNameChange}
                    required
                    className="w-full px-4 py-2.5 rounded-lg text-sm transition-colors"
                    style={{
                      border: firstNameError ? '1px solid #dc2626' : '1px solid rgba(169, 201, 232, 0.3)',
                      backgroundColor: '#FFFFFF',
                      color: '#1B211A',
                      outline: 'none'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = firstNameError ? '#dc2626' : 'rgb(169, 201, 232)';
                    }}
                    onBlur={(e) => {
                      const err = validateFirstName(firstName);
                      setFirstNameError(err);
                      e.target.style.borderColor = err ? '#dc2626' : 'rgba(169, 201, 232, 0.3)';
                    }}
                  />
                  {firstNameError && (
                    <div className="mt-1.5 text-xs" style={{ color: '#dc2626' }}>
                      {firstNameError}
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#1B211A' }}>
                    {t('register.lastName')}
                  </label>
                  <input
                    type="text"
                    value={lastName}
                    onChange={handleLastNameChange}
                    required
                    className="w-full px-4 py-2.5 rounded-lg text-sm transition-colors"
                    style={{
                      border: lastNameError ? '1px solid #dc2626' : '1px solid rgba(169, 201, 232, 0.3)',
                      backgroundColor: '#FFFFFF',
                      color: '#1B211A',
                      outline: 'none'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = lastNameError ? '#dc2626' : 'rgb(169, 201, 232)';
                    }}
                    onBlur={(e) => {
                      const err = validateLastName(lastName);
                      setLastNameError(err);
                      e.target.style.borderColor = err ? '#dc2626' : 'rgba(169, 201, 232, 0.3)';
                    }}
                  />
                  {lastNameError && (
                    <div className="mt-1.5 text-xs" style={{ color: '#dc2626' }}>
                      {lastNameError}
                    </div>
                  )}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#1B211A' }}>
                  {t('register.emailLabel')}
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={handleEmailChange}
                  required
                  className="w-full px-4 py-2.5 rounded-lg text-sm transition-colors"
                  style={{
                    border: emailError ? '1px solid #dc2626' : '1px solid rgba(169, 201, 232, 0.3)',
                    backgroundColor: '#FFFFFF',
                    color: '#1B211A',
                    outline: 'none'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = emailError ? '#dc2626' : 'rgb(169, 201, 232)';
                  }}
                  onBlur={(e) => {
                    const err = validateEmail(email);
                    setEmailError(err);
                    e.target.style.borderColor = err ? '#dc2626' : 'rgba(169, 201, 232, 0.3)';
                  }}
                />
                {emailError && (
                  <div className="mt-1.5 text-xs" style={{ color: '#dc2626' }}>
                    {emailError}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#1B211A' }}>
                  {t('register.passwordLabel')}
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={handlePasswordChange}
                  required
                  className="w-full px-4 py-2.5 rounded-lg text-sm transition-colors"
                  style={{
                    border: passwordError ? '1px solid #dc2626' : '1px solid rgba(169, 201, 232, 0.3)',
                    backgroundColor: '#FFFFFF',
                    color: '#1B211A',
                    outline: 'none'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = passwordError ? '#dc2626' : 'rgb(169, 201, 232)';
                  }}
                  onBlur={(e) => {
                    const err = validatePassword(password);
                    setPasswordError(err);
                    e.target.style.borderColor = err ? '#dc2626' : 'rgba(169, 201, 232, 0.3)';
                  }}
                />
                {passwordError && (
                  <div className="mt-1.5 text-xs" style={{ color: '#dc2626' }}>
                    {passwordError}
                  </div>
                )}
                {!passwordError && password && (
                  <div className="mt-1.5 text-xs" style={{ color: 'rgb(100, 130, 160)' }}>
                    {t('register.passwordRequirementsHint')}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#1B211A' }}>
                  {t('register.confirmPassword')}
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={handleConfirmPasswordChange}
                  required
                  className="w-full px-4 py-2.5 rounded-lg text-sm transition-colors"
                  style={{
                    border: confirmPasswordError ? '1px solid #dc2626' : '1px solid rgba(169, 201, 232, 0.3)',
                    backgroundColor: '#FFFFFF',
                    color: '#1B211A',
                    outline: 'none'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = confirmPasswordError ? '#dc2626' : 'rgb(169, 201, 232)';
                  }}
                  onBlur={(e) => {
                    const err = validateConfirmPassword(confirmPassword);
                    setConfirmPasswordError(err);
                    e.target.style.borderColor = err ? '#dc2626' : 'rgba(169, 201, 232, 0.3)';
                  }}
                />
                {confirmPasswordError && (
                  <div className="mt-1.5 text-xs" style={{ color: '#dc2626' }}>
                    {confirmPasswordError}
                  </div>
                )}
              </div>

              <div>
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey={RECAPTCHA_SITE_KEY}
                  onChange={handleCaptchaChange}
                  onExpired={handleCaptchaExpired}
                  onError={handleCaptchaError}
                />
                {captchaError && (
                  <div className="mt-1.5 text-xs" style={{ color: '#dc2626' }}>
                    {captchaError}
                  </div>
                )}
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-2.5 rounded-full text-sm font-semibold transition-all flex items-center justify-center gap-2"
                style={{
                  backgroundColor: loading ? 'rgb(100, 130, 160)' : 'rgb(169, 201, 232)',
                  color: '#FFFFFF',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.7 : 1
                }}
              >
                {loading ? t('register.registering') : (
                  <>
                    {t('auth.createAccount')}
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </form>

            <p className="text-center text-xs mt-6" style={{ color: 'rgb(100, 130, 160)' }}>
              {t('register.alreadyHaveAccount')}{' '}
              <Link 
                to="/login" 
                className="font-medium hover:opacity-80 transition-opacity"
                style={{ color: 'rgb(169, 201, 232)' }}
              >
                {t('register.loginHere')}
              </Link>
            </p>
          </div>
        </div>
      </main>

      {/* Confirm-email toast (info style, distinct from success green) */}
      {toast.message && (
        <div
          role="alert"
          className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-50 max-w-md rounded-lg px-4 py-3 text-sm font-medium shadow-lg transition-opacity duration-1000 ${
            toast.hiding ? 'opacity-0' : 'opacity-100'
          } bg-amber-500 text-white`}
        >
          {toast.message}
        </div>
      )}
    </div>
  );
}

export default Register;
